/**
 * Client-side install-prompt telemetry.
 *
 * Fires both a GA4 dataLayer event and a first-party row in
 * `pwa_install_events`, which powers the admin install report.
 */
import { getAnonymousId } from "@/lib/identity/anonymousId";
import { recordInstallPromptEvent } from "./install-analytics.functions";

export type InstallPromptEvent =
  | "shown"
  | "clicked"
  | "accepted"
  | "dismissed"
  | "installed"
  | "error";

const GA_EVENT: Record<InstallPromptEvent, string> = {
  shown: "pwa_install_prompt_shown",
  clicked: "pwa_install_clicked",
  accepted: "pwa_install_accepted",
  dismissed: "pwa_install_dismissed",
  installed: "pwa_installed",
  error: "pwa_install_error",
};

export function trackInstallPrompt(
  event: InstallPromptEvent,
  opts: { surface: string; platform?: "android" | "ios" | "unknown" },
): void {
  if (typeof window === "undefined") return;
  const route = window.location.pathname;

  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: GA_EVENT[event],
      pwa_install_surface: opts.surface,
      pwa_install_platform: opts.platform ?? "unknown",
      pwa_install_route: route,
    });
  } catch {
    /* analytics is never load-bearing */
  }

  void recordInstallPromptEvent({
    data: {
      event,
      platform: opts.platform ?? "unknown",
      surface: opts.surface.slice(0, 40),
      route,
      anonymousId: getAnonymousId() || undefined,
      userAgent: navigator.userAgent.slice(0, 400),
    },
  }).catch(() => {
    /* ignore */
  });
}
